import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  TextField,
} from '@mui/material';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import type { StripeCardElementChangeEvent } from '@stripe/stripe-js';
import { getStripePublishableKey, initiatePayment } from '../../api/shop';
import SecurityIcon from '@mui/icons-material/Security';
import LockIcon from '@mui/icons-material/Lock';

// Stripe configuration
let stripePromise: Promise<Stripe | null> | null = null;

const getStripePromise = async () => {
  if (!stripePromise) {
    try {
      const { publishableKey } = await getStripePublishableKey();
      stripePromise = loadStripe(publishableKey);
    } catch (error) {
      console.error('Failed to load Stripe:', error);
      throw error;
    }
  }
  return stripePromise;
};

interface StripePaymentProps {
  onError?: (error: string) => void;
  onCardComplete?: (complete: boolean) => void;
  onCardholderNameChange?: (name: string) => void;
  cardholderName?: string;
  onPaymentSuccess?: (orderId: number) => void;
  orderId?: number | null;
  isProcessing?: boolean;
  setIsProcessing?: (processing: boolean) => void;
  onPaymentReady?: (paymentHandler: (orderId?: number) => Promise<void>) => void;
}

// Credit Card Form Component
const CreditCardForm = ({ 
  onError,
  onCardComplete,
  onCardholderNameChange,
  cardholderName = "",
  onPaymentSuccess,
  orderId,
  setIsProcessing,
  onPaymentReady
}: StripePaymentProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [errorMessage, setErrorMessage] = useState("");
  const orderIdRef = useRef(orderId);

  // Update ref when orderId changes
  useEffect(() => {
    orderIdRef.current = orderId;
  }, [orderId]);

  const handlePayment = useCallback(async (providedOrderId?: number) => {
    const currentOrderId = providedOrderId || orderIdRef.current;
    console.log('handlePayment called with:', { stripe: !!stripe, elements: !!elements, orderId: currentOrderId });
    
    if (!stripe || !elements || !currentOrderId) {
      const errorMsg = "Payment system not ready";
      console.log('Payment system not ready:', { stripe: !!stripe, elements: !!elements, orderId: currentOrderId });
      setErrorMessage(errorMsg);
      onError?.(errorMsg);
      return;
    }

    // Type guard to ensure currentOrderId is a number
    if (typeof currentOrderId !== 'number') {
      const errorMsg = "Payment system not ready - Invalid order ID";
      console.log('Payment system not ready - Invalid order ID:', { currentOrderId });
      setErrorMessage(errorMsg);
      onError?.(errorMsg);
      return;
    }

    setIsProcessing?.(true);
    setErrorMessage("");

    try {
      // Get card element
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error("Card element not found");
      }

      // Create payment method
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: cardholderName,
        },
      });

      if (pmError) {
        throw new Error(pmError.message);
      }

      // Initiate payment with our backend
      console.log('Calling initiatePayment API with:', { orderId: currentOrderId, paymentMethodId: paymentMethod.id });
      const data = await initiatePayment({
        orderId: currentOrderId,
        paymentMethodId: paymentMethod.id
      });
      console.log('initiatePayment API response:', data);

      if (data.error) {
        throw new Error(data.error);
      }

      // Only confirm payment if status is not "succeeded"
      if (data.status !== 'succeeded') {
        if (!data.client_secret) {
          throw new Error("Payment confirmation required but no client secret received");
        }

        // Confirm payment with Stripe
        const { error: confirmError } = await stripe.confirmCardPayment(data.client_secret);

        if (confirmError) {
          throw new Error(confirmError.message);
        }
      }

      // Payment successful (either already succeeded or confirmed successfully)
      onPaymentSuccess?.(currentOrderId);
      
    } catch (error: unknown) {
      console.error("Payment error:", error);
      const errorMsg = error instanceof Error ? error.message : "Payment failed. Please try again.";
      setErrorMessage(errorMsg);
      onError?.(errorMsg);
    } finally {
      setIsProcessing?.(false);
    }
  }, [stripe, elements, cardholderName, onError, onPaymentSuccess, setIsProcessing]);

  // Listen for card changes to track completion status only
  useEffect(() => {
    if (!stripe || !elements) {
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      return;
    }

    // Listen for card changes to track completion status
    const handleCardChange = (event: StripeCardElementChangeEvent) => {
      onCardComplete?.(event.complete);
      
      // Only clear error messages, don't trigger validation
      if (event.complete) {
        setErrorMessage("");
      }
    };

    cardElement.on('change', handleCardChange);

    return () => {
      cardElement.off('change', handleCardChange);
    };
  }, [stripe, elements, onCardComplete]);

  // Expose handlePayment to parent component
  useEffect(() => {
    console.log('Exposing handlePayment:', { stripe: !!stripe, elements: !!elements, orderId });
    
    // Expose via prop if available - call as soon as Stripe and elements are ready
    if (onPaymentReady && stripe && elements) {
      onPaymentReady(handlePayment);
      console.log('handlePayment exposed via prop');
    }
    
    // Also expose via window for backward compatibility
    if (typeof window !== 'undefined') {
      (window as unknown as Record<string, unknown>).handleStripePayment = handlePayment;
      console.log('handleStripePayment exposed to window');
    }
    
    return () => {
      if (typeof window !== 'undefined') {
        delete (window as unknown as Record<string, unknown>).handleStripePayment;
        console.log('handleStripePayment removed from window');
      }
    };
  }, [stripe, elements, handlePayment, onPaymentReady]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Name on Card */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography sx={{ fontSize: 14, color: '#3D3C3C', fontWeight: 500 }}>
          Name on card
        </Typography>
        <TextField
          placeholder="Enter cardholder name"
          value={cardholderName}
          onChange={(e) => onCardholderNameChange?.(e.target.value)}
          fullWidth
          size="small"
          sx={{
            backgroundColor: '#fff',
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: '#E1DACA',
              },
              '&:hover fieldset': {
                borderColor: '#E1DACA',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#E1DACA',
              },
            },
          }}
        />
      </Box>

      {/* Credit Card Input */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography sx={{ fontSize: 14, color: '#3D3C3C', fontWeight: 500 }}>
          Card number, expiry date and CVC
        </Typography>
        <Box
          sx={{
            p: 2,
            border: '1px solid #E1DACA',
            borderRadius: 1,
            backgroundColor: '#fff',
          }}
        >
          <CardElement 
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#424770',
                  fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
                  fontSmoothing: 'antialiased',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                },
                invalid: {
                  color: '#9e2146',
                },
              },
              hidePostalCode: true,
            }}
            onChange={(event: StripeCardElementChangeEvent) => {
              onCardComplete?.(event.complete);
              // Don't show errors during input, only track completion
            }}
          />
        </Box>
      </Box>
      
      <Typography sx={{ fontSize: 12, color: '#6B6B6B' }}>
        Your payment information is encrypted and secure.
      </Typography>
      
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}
    </Box>
  );
};

// Main StripePayment component
const StripePayment = ({ 
  onError,
  onCardComplete,
  onCardholderNameChange,
  cardholderName,
  onPaymentSuccess,
  orderId,
  isProcessing,
  setIsProcessing,
  onPaymentReady
}: StripePaymentProps) => {
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeStripe = async () => {
      try {
        setLoading(true);
        const promise = getStripePromise();
        setStripePromise(promise);
      } catch (err) {
        setError('Failed to initialize payment system');
        console.error('Stripe initialization error:', err);
      } finally {
        setLoading(false);
      }
    };

    initializeStripe();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!stripePromise) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        Payment system not available
      </Alert>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 3,
        backgroundColor: '#FCFAF6',
        border: '1px solid #E1DACA',
        borderRadius: 2,
      }}
    >
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Payment Details
      </Typography>
      
      {/* Security Message */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1, 
        mb: 3,
        color: '#6B6B6B',
        fontSize: 14
      }}>
        <SecurityIcon sx={{ fontSize: 16 }} />
        <LockIcon sx={{ fontSize: 16 }} />
        <Typography sx={{ fontSize: 14, color: '#6B6B6B' }}>
          Secure payment with Stripe
        </Typography>
      </Box>
      
      <Elements stripe={stripePromise}>
        <CreditCardForm 
          onError={onError}
          onCardComplete={onCardComplete}
          onCardholderNameChange={onCardholderNameChange}
          cardholderName={cardholderName}
          onPaymentSuccess={onPaymentSuccess}
          orderId={orderId}
          isProcessing={isProcessing}
          setIsProcessing={setIsProcessing}
          onPaymentReady={onPaymentReady}
        />
      </Elements>
    </Paper>
  );
};

export default StripePayment; 